import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { buttonVariants } from '@/components/ui/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { clinic, whatsappLink } from '@/constants/clinic'

import {
    Instagram,
    Mail,
    MapPin,
    MessageCircle,
    Navigation,
    Phone
} from 'lucide-react'

type ContactItem = {
    label: string
    value: string
    detail?: string
    href?: string
    external?: boolean
    icon: LucideIcon
}

const contactItems: ContactItem[] = [
    {
        label: 'Alamat',
        value: clinic.address,
        detail: clinic.landmark,
        icon: MapPin
    },
    {
        label: 'Telepon',
        value: clinic.phone.label,
        href: clinic.phone.href,
        icon: Phone
    },
    {
        label: 'WhatsApp',
        value: clinic.whatsapp.label,
        href: clinic.whatsapp.href,
        external: true,
        icon: MessageCircle
    },
    {
        label: 'Email',
        value: clinic.email.label,
        href: clinic.email.href,
        icon: Mail
    },
    {
        label: 'Instagram',
        value: clinic.instagram.label,
        href: clinic.instagram.href,
        external: true,
        icon: Instagram
    }
]

const LocationSection: FC = (): ReactNode => {
    return (
        <section id='lokasi-kontak' aria-labelledby='lokasi-kontak-title' className='bg-white py-20 sm:py-24'>
            <div className='mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-14 lg:px-8'>
                <div>
                    <SectionHeading
                        id='lokasi-kontak-title'
                        eyebrow='Kunjungi kami'
                        title='Lokasi dan kontak'
                        description='Klinik berada di Jl. Kerkof, Leuwigajah, tepat di seberang Borma Toserba Kerkof.'
                    />

                    <dl className='mt-8 grid gap-3'>
                        {contactItems.map(item => {
                            const Icon = item.icon

                            return (
                                <div key={item.label} className='flex items-start gap-4 rounded-2xl p-4 ring-1 ring-border transition-colors hover:bg-muted/40'>
                                    <span className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <div className='min-w-0'>
                                        <dt className='text-sm text-muted-foreground'>
                                            {item.label}
                                        </dt>

                                        <dd className='mt-0.5 font-semibold break-words text-ink'>
                                            {item.href ? (
                                                <a
                                                    href={item.href}
                                                    target={item.external ? '_blank' : undefined}
                                                    rel={item.external ? 'noopener noreferrer' : undefined}
                                                    className='break-all underline-offset-4 transition-colors hover:text-brand-700 hover:underline sm:break-words'
                                                >
                                                    {item.value}
                                                </a>
                                            ) : item.value}

                                            {item.detail && (
                                                <span className='mt-1 block text-sm font-normal text-muted-foreground'>
                                                    {item.detail}
                                                </span>
                                            )}
                                        </dd>
                                    </div>
                                </div>
                            )
                        })}
                    </dl>

                    <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                        <a
                            href={clinic.mapsUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={buttonVariants({ variant: 'outline', size: 'lg' })}
                        >
                            <Navigation className='size-5 text-brand-600' aria-hidden='true' />
                            Buka di Google Maps
                        </a>

                        <a
                            href={whatsappLink('Halo Klinik Sehat Budi Luhur, saya ingin bertanya.')}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={buttonVariants({ size: 'lg' })}
                        >
                            <MessageCircle className='size-5' aria-hidden='true' />
                            Chat WhatsApp
                        </a>
                    </div>
                </div>

                <div className='relative min-h-[22rem] overflow-hidden rounded-3xl bg-muted shadow-sm ring-1 ring-border sm:min-h-[28rem] lg:min-h-0'>
                    <iframe
                        src={clinic.mapsEmbedUrl}
                        title={`Peta lokasi ${clinic.name}`}
                        loading='lazy'
                        referrerPolicy='no-referrer-when-downgrade'
                        className='absolute inset-0 size-full border-0'
                        allowFullScreen
                    />

                    <div className='pointer-events-none absolute inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-5 sm:left-5 sm:max-w-sm'>
                        <div className='pointer-events-auto flex items-start gap-3 rounded-2xl bg-white/95 p-4 shadow-xl shadow-ink/10 ring-1 ring-black/5 backdrop-blur'>
                            <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white'>
                                <MapPin className='size-5' aria-hidden='true' />
                            </span>

                            <div className='min-w-0 text-sm'>
                                <p className='font-bold text-ink'>
                                    {clinic.name}
                                </p>

                                <p className='mt-0.5 text-muted-foreground'>
                                    Jl. Kerkof No. 32, Leuwigajah, Cimahi Selatan
                                </p>

                                <a
                                    href={clinic.mapsUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='mt-1.5 inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline'
                                >
                                    Petunjuk arah
                                    <Navigation className='size-3.5' aria-hidden='true' />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default LocationSection
